import { Button, Input } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { authApi } from '../../api/auth.js';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import { useApresConnexion } from '../../auth/session.js';
import { cheminConditions, estRefusCgu } from '../../auth/cgu.js';
import { cheminDoubleAuthentification, estRefusEnrolementMfa } from '../../auth/confinement.js';
import { AuthCard } from '../AuthCard.js';
import { Field } from '../Field.js';
import styles from '../connexion/ConnexionPage.module.css';
import invitationStyles from './InvitationPage.module.css';
import { urlVueDAigle } from './versLaConsole.js';

/** `/invitation/:token` : accepter, choisir un mot de passe, ouvrir la session. */
export function InvitationPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const params = useParams<{ token: string }>();
  const jeton = params.token ?? '';
  const navigate = useNavigate();
  const apresConnexion = useApresConnexion();

  const [nom, setNom] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Invitation de plateforme acceptée : la console est un autre site, avec son
  // adresse (`null` quand ni l'API ni la régie ne la connaissent).
  const [accesConsole, setAccesConsole] = useState<{ vueDAigle: string | null } | null>(null);

  async function soumettre(): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await authApi.invitationAccepter(jeton, {
        motDePasse,
        ...(nom ? { nom } : {}),
      });
      await apresConnexion();
      if (reponse.galerie === null) {
        // Invitation d'associé : aucun compte de galerie, la régie n'a rien à
        // lui montrer. Sa page est la vue d'aigle de la console.
        if (reponse.role !== 'venio_associe') {
          navigate('/');
          return;
        }
        setAccesConsole({ vueDAigle: urlVueDAigle(reponse.consoleUrl) });
        return;
      }
      navigate(`/${reponse.galerie.slug}`);
    } catch (error) {
      if (estRefusCgu(error)) {
        // Le compte existant est connecté, mais sa session doit d'abord
        // accepter les CGU (ARTO-59) : le lien n'a pas été consommé, on y
        // revient une fois les conditions acceptées.
        navigate(cheminConditions(`/invitation/${encodeURIComponent(jeton)}`));
        return;
      }
      if (estRefusEnrolementMfa(error)) {
        // De même quand la grâce pour activer la double authentification est échue.
        navigate(cheminDoubleAuthentification(`/invitation/${encodeURIComponent(jeton)}`));
        return;
      }
      if (estApiError(error)) {
        setErreur(error.message);
      } else if (estErreurReseau(error)) {
        setErreur(t('auth.connexion.erreurReseau'));
      } else {
        setErreur(t('commun.erreurReseau'));
      }
    } finally {
      setEnCours(false);
    }
  }

  if (accesConsole) {
    return (
      <AuthCard title={t('auth.invitation.accesPretTitre')}>
        <div role="status" className={invitationStyles.accesPret}>
          <p>{t('auth.invitation.accesAssocie')}</p>
          <p>{t('auth.invitation.doubleAuthentification')}</p>
          {accesConsole.vueDAigle ? (
            <a className={invitationStyles.lienConsole} href={accesConsole.vueDAigle}>
              {t('auth.invitation.ouvrirConsole')}
            </a>
          ) : (
            <p>{t('auth.invitation.consoleSansAdresse')}</p>
          )}
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.invitation.titre')} subtitle={t('auth.invitation.sousTitre')}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void soumettre();
        }}
        noValidate
      >
        <Field label={t('auth.invitation.champNom')} htmlFor="invitation-nom">
          <Input
            id="invitation-nom"
            autoComplete="name"
            className={styles.pleineLargeur}
            value={nom}
            onChange={(event) => setNom(event.target.value)}
          />
        </Field>
        <Field label={t('auth.invitation.champMotDePasse')} htmlFor="invitation-mot-de-passe">
          <Input
            id="invitation-mot-de-passe"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            className={styles.pleineLargeur}
            value={motDePasse}
            onChange={(event) => setMotDePasse(event.target.value)}
          />
        </Field>

        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}

        <Button type="submit" className={styles.envoi} loading={enCours}>
          {t('auth.invitation.bouton')}
        </Button>
      </form>
    </AuthCard>
  );
}
